/**
 * Les dépenses du studio, et les types qui servent à les ranger.
 *
 * Les types sont enregistrés par le propriétaire (et non figés dans le code) :
 * c'est eux qui permettent de filtrer les rapports et de voir où part l'argent.
 * Les deux vivent sur la même page — un type n'a d'intérêt que pour classer
 * des dépenses.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Typography, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Chip, CircularProgress, Alert, Tooltip, Card,
  CardContent, InputAdornment, Tabs, Tab, Switch, FormControlLabel,
} from '@mui/material';
import { Edit, Delete, Add, Search, Payments, Label } from '@mui/icons-material';
import { toast } from 'react-toastify';

import {
  depenseStudioAPI, typeDepenseStudioAPI, extraireListe,
} from '../../../services/studioAPI';

const htg = (valeur) => `${Number(valeur || 0).toLocaleString()} HTG`;

const aujourdHui = () => new Date().toISOString().slice(0, 10);

const premierDuMois = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().slice(0, 10);
};

const DEPENSE_VIDE = {
  type_depense: '', montant: '', description: '',
  fournisseur: '', date: aujourdHui(), notes: '',
};
const TYPE_VIDE = { nom: '', description: '', est_actif: true };

const Depenses = () => {
  const [onglet, setOnglet] = useState(0);
  const [depenses, setDepenses] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);

  const [dateDebut, setDateDebut] = useState(premierDuMois());
  const [dateFin, setDateFin] = useState(aujourdHui());
  const [filtreType, setFiltreType] = useState('');
  const [recherche, setRecherche] = useState('');

  const [dialogDepense, setDialogDepense] = useState(false);
  const [dialogType, setDialogType] = useState(false);
  const [enEdition, setEnEdition] = useState(false);
  const [formDepense, setFormDepense] = useState(DEPENSE_VIDE);
  const [formType, setFormType] = useState(TYPE_VIDE);

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      setErreur(null);
      const params = { date_debut: dateDebut, date_fin: dateFin };
      if (filtreType) params.type_depense = filtreType;

      const [reponseDepenses, reponseTypes] = await Promise.all([
        depenseStudioAPI.getAll(params),
        typeDepenseStudioAPI.getAll(),
      ]);
      setDepenses(extraireListe(reponseDepenses));
      setTypes(extraireListe(reponseTypes));
    } catch (error) {
      const message = error.response?.status === 403
        ? "Les dépenses sont réservées au propriétaire et au manager"
        : (error.response?.data?.detail || error.message || 'Erreur lors du chargement');
      setErreur(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin, filtreType]);

  useEffect(() => { charger(); }, [charger]);

  const affichees = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    if (!terme) return depenses;
    return depenses.filter((depense) =>
      `${depense.description} ${depense.fournisseur || ''} ${depense.type_depense_nom || ''}`
        .toLowerCase().includes(terme)
    );
  }, [depenses, recherche]);

  const total = affichees.reduce((somme, d) => somme + Number(d.montant || 0), 0);

  const parType = useMemo(() => {
    const cumuls = new Map();
    affichees.forEach((depense) => {
      const nom = depense.type_depense_nom || '—';
      cumuls.set(nom, (cumuls.get(nom) || 0) + Number(depense.montant || 0));
    });
    return [...cumuls.entries()].sort((a, b) => b[1] - a[1]);
  }, [affichees]);

  // --- Dépenses ---

  const ouvrirDialogDepense = (depense = null) => {
    if (depense) {
      setFormDepense({
        id: depense.id,
        type_depense: depense.type_depense,
        montant: depense.montant,
        description: depense.description,
        fournisseur: depense.fournisseur || '',
        date: depense.date,
        notes: depense.notes || '',
      });
      setEnEdition(true);
    } else {
      setFormDepense({ ...DEPENSE_VIDE, type_depense: types[0]?.id || '' });
      setEnEdition(false);
    }
    setDialogDepense(true);
  };

  const enregistrerDepense = async () => {
    if (!formDepense.type_depense) return toast.error('Choisissez un type de dépense');
    if (!formDepense.montant || Number(formDepense.montant) <= 0) {
      return toast.error('Saisissez un montant supérieur à zéro');
    }
    if (!formDepense.description.trim()) return toast.error('Décrivez la dépense');

    try {
      const donnees = { ...formDepense, montant: Number(formDepense.montant) };
      if (enEdition) {
        await depenseStudioAPI.update(formDepense.id, donnees);
        toast.success('Dépense modifiée');
      } else {
        await depenseStudioAPI.create(donnees);
        toast.success('Dépense enregistrée');
      }
      setDialogDepense(false);
      charger();
    } catch (error) {
      toast.error(
        error.response?.data?.montant?.[0] || error.response?.data?.detail
        || "Erreur lors de l'enregistrement"
      );
    }
  };

  const supprimerDepense = async (depense) => {
    if (!window.confirm(`Supprimer cette dépense de ${htg(depense.montant)} ?`)) return;
    try {
      await depenseStudioAPI.delete(depense.id);
      toast.success('Dépense supprimée');
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // --- Types ---

  const ouvrirDialogType = (type = null) => {
    if (type) {
      setFormType({
        id: type.id,
        nom: type.nom,
        description: type.description || '',
        est_actif: type.est_actif,
      });
      setEnEdition(true);
    } else {
      setFormType(TYPE_VIDE);
      setEnEdition(false);
    }
    setDialogType(true);
  };

  const enregistrerType = async () => {
    if (!formType.nom.trim()) return toast.error('Le nom du type est obligatoire');
    try {
      if (enEdition) {
        await typeDepenseStudioAPI.update(formType.id, formType);
        toast.success('Type modifié');
      } else {
        await typeDepenseStudioAPI.create(formType);
        toast.success('Type créé');
      }
      setDialogType(false);
      charger();
    } catch (error) {
      toast.error(
        error.response?.data?.nom?.[0] || error.response?.data?.detail
        || "Erreur lors de l'enregistrement"
      );
    }
  };

  const supprimerType = async (type) => {
    if (!window.confirm(`Supprimer le type « ${type.nom} » ?`)) return;
    try {
      await typeDepenseStudioAPI.delete(type.id);
      toast.success('Type supprimé');
      charger();
    } catch (error) {
      // PROTECT : un type qui porte des dépenses ne part pas
      toast.error('Impossible : des dépenses utilisent encore ce type — désactivez-le plutôt');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Dépenses</Typography>
          <Typography variant="body2" color="text.secondary">
            Ce qui sort de la caisse — c'est ce qui se déduit du bénéfice
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => (onglet === 0 ? ouvrirDialogDepense() : ouvrirDialogType())}
          disabled={onglet === 0 && types.length === 0}
        >
          {onglet === 0 ? 'Nouvelle dépense' : 'Nouveau type'}
        </Button>
      </Box>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
      {types.length === 0 && !erreur && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Créez d'abord un type de dépense (Achat de matériel, Loyer, Électricité...) :
          toute dépense doit être rangée dans un type.
        </Alert>
      )}

      <Tabs value={onglet} onChange={(e, valeur) => setOnglet(valeur)} sx={{ mb: 2 }}>
        <Tab label="Dépenses" icon={<Payments />} iconPosition="start" />
        <Tab label={`Types (${types.length})`} icon={<Label />} iconPosition="start" />
      </Tabs>

      {onglet === 0 ? (
        <>
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <Card sx={{ flex: '1 1 220px' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Total sur la période</Typography>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {htg(total)}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '2 1 380px' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Répartition par type
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {parType.length === 0
                    ? <Typography variant="body2" color="text.disabled">Aucune dépense</Typography>
                    : parType.map(([nom, montant]) => (
                        <Chip key={nom} label={`${nom} · ${htg(montant)}`} size="small" />
                      ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField
              size="small" type="date" label="Du" value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small" type="date" label="Au" value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small" select label="Type" value={filtreType}
              onChange={(e) => setFiltreType(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Tous</MenuItem>
              {types.map((type) => (
                <MenuItem key={type.id} value={type.id}>{type.nom}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small" placeholder="Chercher" value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              sx={{ flex: '1 1 220px' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              }}
            />
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Fournisseur</TableCell>
                  <TableCell align="right">Montant</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {affichees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Payments sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography color="text.secondary" mt={1}>
                        Aucune dépense sur cette période
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : affichees.map((depense) => (
                  <TableRow key={depense.id} hover>
                    <TableCell>{depense.date}</TableCell>
                    <TableCell>
                      <Chip size="small" label={depense.type_depense_nom} variant="outlined" />
                    </TableCell>
                    <TableCell>{depense.description}</TableCell>
                    <TableCell>{depense.fournisseur || '—'}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600} color="error.main">
                        {htg(depense.montant)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => ouvrirDialogDepense(depense)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => supprimerDepense(depense)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Statut</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {types.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Label sx={{ fontSize: 48, color: 'text.disabled' }} />
                    <Typography color="text.secondary" mt={1}>
                      Aucun type de dépense
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : types.map((type) => (
                <TableRow key={type.id} hover>
                  <TableCell><Typography fontWeight={600}>{type.nom}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {type.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={type.est_actif ? 'Actif' : 'Inactif'}
                      color={type.est_actif ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifier">
                      <IconButton size="small" onClick={() => ouvrirDialogType(type)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton size="small" color="error" onClick={() => supprimerType(type)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogue dépense */}
      <Dialog open={dialogDepense} onClose={() => setDialogDepense(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{enEdition ? 'Modifier la dépense' : 'Nouvelle dépense'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" gap={2}>
              <TextField
                label="Type" select fullWidth required
                value={formDepense.type_depense}
                onChange={(e) => setFormDepense({ ...formDepense, type_depense: e.target.value })}
              >
                {types.filter((t) => t.est_actif).map((type) => (
                  <MenuItem key={type.id} value={type.id}>{type.nom}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Date" type="date" fullWidth
                value={formDepense.date}
                onChange={(e) => setFormDepense({ ...formDepense, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <TextField
              label="Montant" type="number" fullWidth required
              value={formDepense.montant}
              onChange={(e) => setFormDepense({ ...formDepense, montant: e.target.value })}
              InputProps={{ endAdornment: <InputAdornment position="end">HTG</InputAdornment> }}
            />
            <TextField
              label="Ce qui a été acheté" fullWidth required
              value={formDepense.description}
              onChange={(e) => setFormDepense({ ...formDepense, description: e.target.value })}
              placeholder="Vernis, capsules, poudre acrylique"
            />
            <TextField
              label="Fournisseur" fullWidth
              value={formDepense.fournisseur}
              onChange={(e) => setFormDepense({ ...formDepense, fournisseur: e.target.value })}
            />
            <TextField
              label="Notes" fullWidth multiline rows={2}
              value={formDepense.notes}
              onChange={(e) => setFormDepense({ ...formDepense, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogDepense(false)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrerDepense}>
            {enEdition ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue type */}
      <Dialog open={dialogType} onClose={() => setDialogType(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{enEdition ? 'Modifier le type' : 'Nouveau type de dépense'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nom" fullWidth required autoFocus
              value={formType.nom}
              onChange={(e) => setFormType({ ...formType, nom: e.target.value })}
              placeholder="Achat de matériel, Loyer, Électricité..."
            />
            <TextField
              label="Description" fullWidth multiline rows={2}
              value={formType.description}
              onChange={(e) => setFormType({ ...formType, description: e.target.value })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formType.est_actif}
                  onChange={(e) => setFormType({ ...formType, est_actif: e.target.checked })}
                />
              }
              label="Proposé à la saisie"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(false)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrerType}>
            {enEdition ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Depenses;
